import React from 'react';
import { createPortal } from 'react-dom';
import {
  formatWorksheetDocumentBytes,
  type ProgramWorksheetListItem,
} from '../../utils/programWorksheetList';
import {
  resolveWorksheetRefLinkState,
  worksheetRefLinkStateLabel,
} from '../../utils/programWorksheetLinkState';
import { fetchWorksheetRefStatsMap, type WorksheetRefStats } from '../../services/programContentWorksheetService';
import { worksheetDocumentMeaningfulPageCount } from '../../types/learningContentWorksheetCanvas';
import {
  loadLearningContentWorksheetDraft,
} from '../../utils/learningContentWorksheetCanvasDraftStorage';
import ModalBodyLoadingSpinner from '../common/ModalBodyLoadingSpinner';
import ProgramPresentationListRowActions from './ProgramPresentationListRowActions';

const SYNC_MIRROR_ROW_TITLE =
  '동기화 미러 — 대표 시간표(원본) 행에서만 열고 편집할 수 있습니다.';

function worksheetListOpenCellProps(
  row: ProgramWorksheetListItem,
  onOpen: () => void,
  extraClass = ''
): React.TdHTMLAttributes<HTMLTableCellElement> {
  if (row.isSyncMirror) {
    return {
      className: `cp-program-worksheet-list-open-cell cp-program-worksheet-list-open-cell-disabled${extraClass ? ` ${extraClass}` : ''}`,
      title: SYNC_MIRROR_ROW_TITLE,
      'aria-disabled': true,
    };
  }
  return {
    className: `cp-program-worksheet-list-open-cell${extraClass ? ` ${extraClass}` : ''}`,
    tabIndex: 0,
    role: 'button',
    onClick: onOpen,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

function collectWorksheetRefIds(items: ProgramWorksheetListItem[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  items.forEach((it) => {
    const id = it.worksheetOwner?.id?.trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

function draftStatsForRow(row: ProgramWorksheetListItem): WorksheetRefStats | null {
  if (!row.hasLocalDraft || row.hasDbDocument) return null;
  const rowKey = (row.draftStorageRowKey ?? row.annualCellKey).trim();
  const draft = loadLearningContentWorksheetDraft(rowKey);
  if (!draft) return null;
  try {
    const json = JSON.stringify(draft);
    return {
      pageCount: worksheetDocumentMeaningfulPageCount(draft),
      documentBytes: new Blob([json]).size,
    };
  } catch {
    return { pageCount: 0, documentBytes: 0 };
  }
}

function applyStatsToItems(
  items: ProgramWorksheetListItem[],
  statsMap: Map<string, WorksheetRefStats>
): ProgramWorksheetListItem[] {
  return items.map((it) => {
    const refId = it.worksheetOwner?.id?.trim() ?? '';
    const dbStats = refId ? statsMap.get(refId) : undefined;
    const draftStats = draftStatsForRow(it);
    const pageCount = dbStats?.pageCount ?? draftStats?.pageCount;
    const documentBytes = dbStats?.documentBytes ?? draftStats?.documentBytes;
    const hasDocument = !!it.hasDbDocument || !!it.hasLocalDraft || !!it.isDraftOnly;
    const linkState = resolveWorksheetRefLinkState({
      hasRef: !!it.hasWorksheetRef,
      hasDocument,
    });
    return {
      ...it,
      pageCount,
      documentBytes,
      isDraftOnly: !it.hasDbDocument && !!it.hasLocalDraft,
      linkState,
      linkStateLabel: worksheetRefLinkStateLabel(linkState),
    };
  });
}

export type ProgramWorksheetListActionState = {
  canCopy: boolean;
  canDelete: boolean;
};

export type ProgramWorksheetListModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  items: ProgramWorksheetListItem[];
  onOpenItem: (annualCellKey: string) => void;
  title?: string;
  getActionState?: (annualCellKey: string) => ProgramWorksheetListActionState;
  onCopy?: (annualCellKey: string) => boolean;
  onDelete?: (annualCellKey: string) => boolean;
  actionsDisabled?: boolean;
  onNotify?: (message: string) => void;
  loading?: boolean;
};

const ProgramWorksheetListModal: React.FC<ProgramWorksheetListModalProps> = ({
  open,
  onClose,
  workspaceId,
  items,
  onOpenItem,
  title = '활동지 목록',
  getActionState,
  onCopy,
  onDelete,
  actionsDisabled = false,
  onNotify,
  loading = false,
}) => {
  const showActions = !!(getActionState && onCopy && onDelete);
  const refIds = React.useMemo(() => collectWorksheetRefIds(items), [items]);
  const refIdsSignature = React.useMemo(() => refIds.slice().sort().join('|'), [refIds]);
  const [statsMap, setStatsMap] = React.useState<Map<string, WorksheetRefStats>>(new Map());
  const [loadingStats, setLoadingStats] = React.useState(false);
  const [statsError, setStatsError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setStatsMap(new Map());
      setStatsError(null);
      setLoadingStats(false);
      return;
    }
    const sid = workspaceId.trim();
    if (!sid || !refIds.length) {
      setStatsMap(new Map());
      setStatsError(null);
      setLoadingStats(false);
      return;
    }
    let cancelled = false;
    setLoadingStats(true);
    setStatsError(null);
    void fetchWorksheetRefStatsMap(sid, refIds)
      .then((map) => {
        if (!cancelled) setStatsMap(map);
      })
      .catch((err) => {
        if (!cancelled) {
          setStatsError(err instanceof Error ? err.message : String(err));
          setStatsMap(new Map());
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStats(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, refIds, refIdsSignature, workspaceId]);

  const rows = React.useMemo(() => {
    if (!open || !items.length) return [] as ProgramWorksheetListItem[];
    return applyStatsToItems(items, statsMap);
  }, [items, open, statsMap]);

  const handleCopy = (annualCellKey: string) => {
    if (!onCopy) return;
    if (onCopy(annualCellKey)) return;
    onNotify?.('복사할 활동지가 없습니다. 먼저 활동지를 만들어 주세요.');
  };

  const handleDelete = (annualCellKey: string) => {
    if (!onDelete) return;
    if (onDelete(annualCellKey)) {
      onNotify?.('해당 학습내용의 활동지가 삭제되었습니다.');
      return;
    }
    onNotify?.('삭제할 활동지가 없습니다.');
  };

  if (!open) return null;

  const bodyLoading = loading || loadingStats;

  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="cp-modal cp-program-worksheet-list-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-program-worksheet-list-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3 id="cp-program-worksheet-list-title">{title}</h3>
        <p className="page-desc cp-program-worksheet-list-intro">
          이 화면에 표시·기록된 활동지입니다. 행을 클릭하면 편집 모달이 열립니다(목록은 유지됩니다). 용량은 저장된
          활동지 데이터 크기(추정)입니다. <strong>연결</strong> 열에서 ref·활동지 조합을 확인할 수 있습니다.
        </p>
        {statsError ? (
          <p className="cp-program-data-cleanup-error" role="alert">
            {statsError}
          </p>
        ) : null}
        <div className="cp-modal-body cp-program-worksheet-list-body" aria-busy={bodyLoading || undefined}>
          {bodyLoading ? (
            <ModalBodyLoadingSpinner label="활동지 목록 불러오는 중" />
          ) : rows.length ? (
            <div className="cp-program-worksheet-list-table-wrap">
              <table className="cp-program-worksheet-list-table">
                <thead>
                  <tr>
                    <th scope="col">월일</th>
                    <th scope="col">요일</th>
                    <th scope="col">교시</th>
                    <th scope="col">편제</th>
                    <th scope="col">시간표명</th>
                    <th scope="col">단원</th>
                    <th scope="col">학습내용</th>
                    <th scope="col">연결</th>
                    <th scope="col">페이지</th>
                    <th scope="col">용량</th>
                    {showActions ? <th scope="col">작업</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isSyncMirror = !!row.isSyncMirror;
                    const openRowHandler = () => onOpenItem(row.annualCellKey);
                    const pageLabel = row.isDraftOnly
                      ? '초안'
                      : row.pageCount != null
                        ? `${row.pageCount}`
                        : '—';
                    const sizeLabel = row.isDraftOnly
                      ? '—'
                      : row.documentBytes != null
                        ? formatWorksheetDocumentBytes(row.documentBytes)
                        : '—';
                    const actionState = showActions
                      ? getActionState!(row.annualCellKey)
                      : { canCopy: false, canDelete: false };
                    const rowClassName = [
                      'cp-program-worksheet-list-row',
                      isSyncMirror ? 'cp-program-worksheet-list-row-sync-mirror' : '',
                      row.linkState === 'ref_no_document' || row.linkState === 'document_no_ref'
                        ? 'cp-program-worksheet-list-row-link-warning'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const rowTitle = isSyncMirror ? SYNC_MIRROR_ROW_TITLE : undefined;
                    const templateTitle = isSyncMirror ? SYNC_MIRROR_ROW_TITLE : row.templateLabel;
                    return (
                      <tr key={row.annualCellKey} className={rowClassName} title={rowTitle}>
                        <td {...worksheetListOpenCellProps(row, openRowHandler)}>{row.dateLabel || '—'}</td>
                        <td {...worksheetListOpenCellProps(row, openRowHandler)}>{row.weekdayLabel || '—'}</td>
                        <td {...worksheetListOpenCellProps(row, openRowHandler)}>{row.periodLabel || '—'}</td>
                        <td {...worksheetListOpenCellProps(row, openRowHandler)} title={templateTitle}>
                          {row.templateLabel || '—'}
                        </td>
                        <td
                          {...worksheetListOpenCellProps(row, openRowHandler)}
                          title={isSyncMirror ? SYNC_MIRROR_ROW_TITLE : row.timetableText}
                        >
                          {row.timetableText || '—'}
                        </td>
                        <td {...worksheetListOpenCellProps(row, openRowHandler)} title={isSyncMirror ? SYNC_MIRROR_ROW_TITLE : row.unitText}>
                          {row.unitText || '—'}
                        </td>
                        <td
                          {...worksheetListOpenCellProps(row, openRowHandler)}
                          title={isSyncMirror ? SYNC_MIRROR_ROW_TITLE : row.learningText}
                        >
                          {row.learningText || '—'}
                        </td>
                        <td
                          {...worksheetListOpenCellProps(row, openRowHandler)}
                          title={row.linkStateLabel || '—'}
                        >
                          {row.linkStateLabel || '—'}
                        </td>
                        <td {...worksheetListOpenCellProps(row, openRowHandler, 'cp-program-worksheet-list-col-num')}>
                          {pageLabel}
                        </td>
                        <td {...worksheetListOpenCellProps(row, openRowHandler, 'cp-program-worksheet-list-col-num')}>
                          {sizeLabel}
                        </td>
                        {showActions ? (
                          <td className="cp-program-worksheet-list-actions-cell">
                            <ProgramPresentationListRowActions
                              annualCellKey={row.annualCellKey}
                              canCopy={actionState.canCopy && !isSyncMirror}
                              canDelete={actionState.canDelete && !isSyncMirror}
                              disabled={actionsDisabled || isSyncMirror}
                              resourceLabel="활동지"
                              onCopy={handleCopy}
                              onDelete={handleDelete}
                            />
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="page-desc">이 화면에 연결된 활동지가 없습니다.</p>
          )}
        </div>
        <div className="cp-modal-footer cp-program-worksheet-list-footer">
          <button type="button" className="cp-btn-secondary" onClick={() => onClose()}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProgramWorksheetListModal;
